import DownloadIcon from '@mui/icons-material/Download';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDownloadOnTopCostsImportTemplate, useImportOnTopCosts } from '../../hooks/api/budgetOnTopCost';
import type { ImportOnTopCostsResponse } from '../../services/budgetOnTopCost';
import ImportDialog from './ImportDialog';

interface OnTopCostsImportDialogProps {
    open: boolean;
    onClose: () => void;
    versionId: number;
    /** Called once an import has actually written rows. The import REPLACES the version's on-top list with
     *  new ids, so the grid uses this to drop pending edits that now point at rows which no longer exist
     *  (CALL-779). */
    onImported?: () => void;
}

const ACCEPT = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx';

const OnTopCostsImportDialog = ({ open, onClose, versionId, onImported }: OnTopCostsImportDialogProps) => {
    const { t } = useTranslation('budget');
    const { mutateAsync: importCosts, isLoading: isImporting } = useImportOnTopCosts(versionId);
    const { mutate: downloadTemplate, isLoading: isDownloadingTemplate } = useDownloadOnTopCostsImportTemplate();

    const importFile = async (file: File) => {
        const result = await importCosts(file);
        // Only when rows were actually written: an all-skipped file leaves the previous import in place
        // (see isComplete below), so the pending edits still point at rows that exist.
        if (result.createdCount > 0) onImported?.();
        return result;
    };

    return (
        <ImportDialog<ImportOnTopCostsResponse>
            open={open}
            onClose={onClose}
            title={t('onTopCosts.import')}
            description={t('import.onTopCostUploadHint')}
            templateAction={
                <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadTemplate()}
                    disabled={isDownloadingTemplate}
                >
                    <span>
                        {isDownloadingTemplate ? t('import.preparingTemplate') : t('import.downloadSampleTemplate')}
                    </span>
                </Button>
            }
            accept={ACCEPT}
            isImporting={isImporting}
            importFile={importFile}
            // Treat an all-skipped import (nothing written) as not done, so the user can fix the
            // file and retry in place instead of seeing a misleading "completed" state. CALL-757:
            // the import writes the file's rows wholesale, so a successful run always has
            // createdCount > 0.
            isComplete={(result) => result.createdCount > 0}
            summaryRows={(result) => [
                { label: t('import.rowsParsed'), value: result.rowsParsed },
                { label: t('import.statusCreated'), value: result.createdCount },
                { label: t('import.statusReplaced'), value: result.replacedCount },
                { label: t('import.statusSkipped'), value: result.skippedCount },
                { label: t('import.monthColumns'), value: result.monthColumns.length },
            ]}
        />
    );
};

export default OnTopCostsImportDialog;
